/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {DefaultEntityForm} from './DefaultEntityForm';
import {providers, client} from '@logilab/cwclientlibjs';
import {
    defaultAttributeValidation,
    EDITION_TIMEOUT,
    ProvidersContext,
} from './commons';

/**
 * Parameters for an entity browser
 */
export interface EntityBrowserProps {
    /**
     * The RQL client to use
     */
    client: client.RqlClient;
    /**
     * The CSS class name(s) for the component
     */
    className: string;
    /**
     * Renders the browser's view when waiting for input
     */
    renderViewWaiting: (component: EntityBrowser) => JSX.Element;
    /**
     * Renders the browser's view for the specified EID
     */
    renderViewData: (component: EntityBrowser) => JSX.Element;
    /**
     * Renders the EID input component
     */
    renderInput: (component: EntityBrowser) => JSX.Element;
    /**
     * Renders the component
     */
    render: (component: EntityBrowser) => JSX.Element;
}

/**
 * The state of an entity browser
 */
export interface EntityBrowserState {
    /**
     * The current timestamp
     */
    timestamp: number;
    /**
     * The current input for the EID
     */

    input: string;
    /**
     * The EID of the entity to display
     */
    eidToShow: number | null;
}

/**
 * The interface of an entity browser
 */
export interface EntityBrowser<
    P extends EntityBrowserProps = EntityBrowserProps,
    S extends EntityBrowserState = EntityBrowserState
> extends React.Component<P, S> {
    /**
     * The schema provider to use
     */
    schemaProvider: providers.EntitySchemaProvider;
    /**
     * The entity provider to use
     */
    entityProvider: providers.EntityProvider;
    /**
     * When the input for EID selection changed
     */
    onInputChanged: (input: string) => void;
    /**
     * When the used clicked on a link to an entity
     */
    onClickGotoTarget: (eid: number) => void;
}

/**
 * The default props for an entity browser
 */
export const ENTITY_BROWSER_DEFAULT_PROPS = {
    className: '',
    renderViewWaiting: (component: EntityBrowser) => {
        return <div>{'Waiting for EID'}</div>;
    },
    renderViewData: (component: EntityBrowser) => {
        return (
            <ProvidersContext.Provider
                value={{
                    schemaProvider: component.schemaProvider,
                    entityProvider: component.entityProvider,
                }}
            >
                <DefaultEntityForm
                    timestamp={component.state.timestamp}
                    eid={component.state.eidToShow!}
                    onClickGotoTarget={component.onClickGotoTarget}
                    onChangeAttribute={defaultAttributeValidation}
                />
            </ProvidersContext.Provider>
        );
    },
    renderInput: (component: EntityBrowser) => {
        return (
            <div>
                <label>{'EID'}</label>
                <input
                    type="number"
                    value={component.state.input}
                    onChange={(event) =>
                        component.onInputChanged(event.currentTarget.value)
                    }
                />
            </div>
        );
    },
    render: (component: EntityBrowser) => {
        return (
            <div className={component.props.className}>
                {component.props.renderInput(component)}
                {component.state.eidToShow === null
                    ? component.props.renderViewWaiting(component)
                    : component.props.renderViewData(component)}
            </div>
        );
    },
};

/**
 * A naviguable browser of entities
 */
export class DefaultEntityBrowser
    extends React.Component<EntityBrowserProps, EntityBrowserState>
    implements EntityBrowser<EntityBrowserProps, EntityBrowserState>
{
    public static defaultProps = ENTITY_BROWSER_DEFAULT_PROPS;
    /**
     * The schema provider to use
     */
    schemaProvider: providers.EntitySchemaProvider;
    /**
     * The entity provider to use
     */
    entityProvider: providers.EntityProvider;
    /**
     * Whether the component is currently mounted
     */
    _isMounted: boolean;
    /**
     * The timeout for the input
     */
    timeout: number | undefined;

    constructor(props: Readonly<EntityBrowserProps>) {
        super(props);
        this.schemaProvider = new providers.RqlEntitySchemaProvider(
            props.client
        );
        this.entityProvider = new providers.RqlEntityProvider(
            props.client,
            this.schemaProvider
        );
        this.timeout = undefined;
        this.state = {
            timestamp: Date.now(),
            input: '',
            eidToShow: null,
        };
        this.onInputChanged = this.onInputChanged.bind(this);
        this.onClickGotoTarget = this.onClickGotoTarget.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        window.clearTimeout(this.timeout);
        this._isMounted = false;
    }

    onInputChanged(input: string) {
        this.setState({input: input});
        clearTimeout(this.timeout);
        this.timeout = window.setTimeout(() => {
            if (this._isMounted) {
                this.setState({
                    eidToShow: Number(this.state.input).valueOf(),
                    timestamp: Date.now(),
                });
            }
        }, EDITION_TIMEOUT);
    }

    onClickGotoTarget(eid: number) {
        this.setState({
            timestamp: Date.now(),
            eidToShow: eid,
            input: eid.toString(),
        });
    }

    render(): JSX.Element {
        return this.props.render(this);
    }
}
